import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from "react";
import { MapContainer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./HDImpactMap.css";

const normalize = (v) =>
  v ? String(v).trim().toLowerCase() : "";

const HDImpactMap = ({
  selectedGender,
  selectedAgeRange,
  selectedState,
  selectedDistrict,
  selectedProjectName,
  selectedDateRange,
  selectedHomeState,
  selectedHomeDistrict,
  selectedOperatingState,
  selectedOperatingDistrict
}) => {
  const mapRef = useRef(null);

  const [stateGeo, setStateGeo] = useState(null);
  const [districtGeo, setDistrictGeo] = useState(null);

  const [stateData, setStateData] = useState([]);
  const [districtData, setDistrictData] = useState([]);

  const [showDistrictMap, setShowDistrictMap] = useState(false);
  const [clickedState, setClickedState] = useState(null);

  /* ================= COMMON PAYLOAD ================= */
  const getPayload = () => ({
    gender: selectedGender || [],
    ageRange: selectedAgeRange || [],
    homeState: selectedHomeState || [],
    homeDistrict: selectedHomeDistrict || [],
    operatingState: selectedOperatingState || [],
    operatingDistrict: selectedOperatingDistrict || [],
    projectName: selectedProjectName || [],
    startDate: selectedDateRange?.start || null,
    endDate: selectedDateRange?.end || null
  });

  /* ================= LOAD STATE GEOJSON ================= */
  useEffect(() => {
    fetch("/india.json")
      .then((res) => res.json())
      .then((data) => {
        setStateGeo(data);
        if (mapRef.current) {
          mapRef.current.fitBounds(L.geoJSON(data).getBounds());
        }
      })
      .catch(() => setStateGeo(null));
  }, []);

  /* ================= RESET DISTRICT ================= */
  useEffect(() => {
    setShowDistrictMap(false);
    setClickedState(null);
    setDistrictGeo(null);
    setDistrictData([]);
  }, [
    selectedGender,
    selectedAgeRange,
    selectedState,
    selectedDistrict,
    selectedProjectName,
    selectedDateRange,
    selectedHomeState,
    selectedOperatingState,
    selectedHomeDistrict,           // ✅ ADD
  selectedOperatingDistrict  
  ]);

  /* ================= FETCH STATE DATA ================= */
  useEffect(() => {
    if (showDistrictMap && clickedState) {
      loadDistricts(clickedState);
      return;
    }

    fetch("https://api-sales-insights.haqdarshak.com/api/hd-impact/map-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(getPayload())
    })
      .then((res) => res.json())
      .then((data) => {
        setStateData(Array.isArray(data) ? data : []);
      })
      .catch(() => setStateData([]));
  }, [
    selectedGender,
    selectedAgeRange,
    selectedState,
    selectedDistrict,
    selectedProjectName,
    selectedDateRange,
    selectedHomeState,
    selectedOperatingState,
    showDistrictMap,
    clickedState,
    selectedHomeDistrict,
selectedOperatingDistrict,
  ]);

  /* ================= LOAD DISTRICTS ================= */
  const loadDistricts = useCallback(
    (stateName) => {
      if (!stateName) return;

      setClickedState(stateName);
      setShowDistrictMap(true);

      fetch("https://api-sales-insights.haqdarshak.com/api/hd-impact/district-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...getPayload(),
          state: stateName,
          useOperating:
            selectedOperatingState?.length ||
            selectedOperatingDistrict?.length
        })
      })
        .then((res) => res.json())
        .then((data) =>
          setDistrictData(Array.isArray(data) ? data : [])
        )
        .catch(() => setDistrictData([]));

      fetch("/india_district.geojson")
        .then((res) => res.json())
        .then((geo) => {
          const filtered = geo.features.filter(
            (f) =>
              normalize(f.properties.st_nm) ===
              normalize(stateName)
          );

          setDistrictGeo({
            type: "FeatureCollection",
            features: filtered
          });

          if (mapRef.current && filtered.length) {
            mapRef.current.fitBounds(
              L.geoJSON(filtered).getBounds()
            );
          }
        });
    },
    [
      selectedGender,
      selectedAgeRange,
      selectedProjectName,
      selectedDateRange,
      selectedHomeState,
      selectedOperatingState
    ]
  );

  /* ================= AUTO DISTRICT LOAD ================= */
  useEffect(() => {
    if (selectedDistrict?.length && selectedState?.length) {
      loadDistricts(selectedState[0]);
    }
  }, [selectedDistrict, selectedState, loadDistricts]);

  /* ================= LOOKUP MAP ================= */
  const stateMap = useMemo(() => {
    const obj = {};
    stateData.forEach((s) => {
      obj[normalize(s.State)] = s;
    });
    return obj;
  }, [stateData]);

  const districtMap = useMemo(() => {
    const obj = {};
    districtData.forEach((d) => {
      obj[
        `${normalize(d.State)}__${normalize(d.District)}`
      ] = d;
    });
    return obj;
  }, [districtData]);

  /* ================= COLOR ================= */
  const getColor = (value) =>
    value > 5000
      ? "#4B0B8E"
      : value > 2000
      ? "#6D4E94"
      : value > 1000
      ? "#8E94B0"
      : value > 500
      ? "#B8C9D5"
      : value > 0
      ? "#E3F2FD"
      : "#f2f2f2";

  /* ================= STYLE ================= */
  const stateStyle = (feature) => ({
    fillColor: getColor(
      stateMap[normalize(feature.properties.st_nm)]
        ?.totalCount ?? 0
    ),
    weight: 1,
    color: "#fff",
    fillOpacity: 0.85
  });

  const districtStyle = (feature) => {
    const stateName = feature.properties.st_nm;
    const districtName = feature.properties.NAME_2;
  
    const key = `${normalize(stateName)}__${normalize(districtName)}`;
  
    const selectedDistricts =
      selectedOperatingDistrict?.length
        ? selectedOperatingDistrict
        : selectedHomeDistrict;
  
    const isSelected =
      selectedDistricts?.length
        ? selectedDistricts.some(
            (d) => normalize(d) === normalize(districtName)
          )
        : true;
  
    const value = isSelected
      ? districtMap[key]?.totalCount ?? 0
      : 0;
  
    return {
      fillColor: isSelected ? getColor(value) : "#f2f2f2", // ❌ no color for others
      weight: 1,
      color: "#999", // keep boundary visible
      fillOpacity: isSelected ? 0.85 : 0.3
    };
  };

  /* ================= POPUPS ================= */
  const onEachState = (feature, layer) => {
    const stateName = feature.properties.st_nm;
    const data = stateMap[normalize(stateName)] || {};

    const btnId = `btn-${normalize(stateName)}`;

    layer.bindPopup(`
      <div><b>${stateName.toUpperCase()}</b></div>
      <div>Males: ${data.maleCount || 0}</div>
      <div>Females: ${data.femaleCount || 0}</div>
      <div>Others: ${data.othersCount || 0}</div>
      <div><b>Total: ${data.totalCount || 0}</b></div>
      <br/>
      <button id="${btnId}">Show Districts</button>
    `);

    layer.on("popupopen", () => {
      setTimeout(() => {
        const btn = document.getElementById(btnId);
        if (btn) btn.onclick = () => loadDistricts(stateName);
      }, 0);
    });
  };

  const onEachDistrict = (feature, layer) => {
    const stateName = feature.properties.st_nm;
    const districtName = feature.properties.NAME_2;

    const data =
      districtMap[
        `${normalize(stateName)}__${normalize(districtName)}`
      ] || {};

    layer.bindPopup(`
      <div><b>${districtName.toUpperCase()}</b></div>
      <div>State: ${stateName}</div>
      <div>Males: ${data.maleCount || 0}</div>
      <div>Females: ${data.femaleCount || 0}</div>
      <div>Others: ${data.othersCount || 0}</div>
      <div><b>Total: ${data.totalCount || 0}</b></div>
    `);
  };

  /* ================= BACK ================= */
  const handleBack = () => {
    setShowDistrictMap(false);
    setClickedState(null);
    setDistrictGeo(null);
    setDistrictData([]);

    fetch("https://api-sales-insights.haqdarshak.com/api/hd-impact/map-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(getPayload())
    })
      .then((res) => res.json())
      .then((data) =>
        setStateData(Array.isArray(data) ? data : [])
      );

    if (mapRef.current && stateGeo) {
      mapRef.current.fitBounds(
        L.geoJSON(stateGeo).getBounds()
      );
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="hd-impact-map">
      <MapContainer
        zoom={5}
        center={[22.9, 82.6]}
        style={{ height: "100%", width: "100%" }}
        whenCreated={(map) => (mapRef.current = map)}
        scrollWheelZoom={false}
      >
        {!showDistrictMap && stateGeo && (
          <GeoJSON
          key={
            JSON.stringify(stateData) +
            JSON.stringify(selectedOperatingState) +
            JSON.stringify(selectedHomeState)
          }
            data={stateGeo}
            style={stateStyle}
            onEachFeature={onEachState}
          />
        )}

        {showDistrictMap && districtGeo && (
          <>
            <GeoJSON
  key={
    JSON.stringify(districtData) +
    JSON.stringify(selectedOperatingDistrict) +
    JSON.stringify(selectedHomeDistrict)
  }
              data={districtGeo}
              style={districtStyle}
              onEachFeature={onEachDistrict}
            />

            <button className="back-button" onClick={handleBack}>
              Back to State Map
            </button>
          </>
        )}
      </MapContainer>
    </div>
  );
};

export default HDImpactMap;